/**
 * The per-assembly link the war room shares: https://<field-host>/?a=HAR_01
 * (optionally &n=<display name>, shown before login because the API has no public endpoint
 * for an assembly's name).
 */
import type { FieldUser } from "./api";

export const ASSEMBLY_ID = /^[A-Z]{2,3}_[A-Z0-9_]{1,20}$/;

export interface AssemblyLink {
  assemblyId: string;
  name: string | null;
}

export function parseAssemblyLink(search: string): AssemblyLink | null {
  const q = new URLSearchParams(search);
  const a = (q.get("a") ?? "").trim().toUpperCase();
  if (!ASSEMBLY_ID.test(a)) return null;
  const n = (q.get("n") ?? "").trim().slice(0, 80);
  return { assemblyId: a, name: n || null };
}

export interface AssemblyChoice {
  /** the assembly to open, or null = the user must pick one (super admin without a link) */
  assemblyId: string | null;
  /** the link pointed at an assembly this login cannot open */
  linkMismatch: boolean;
}

/**
 * The login decides the assembly (N-PAC, 4 Nov 2026: "आइडी पासवर्ड डालने पर खुद विधानसभा उठा ले"): surveyors and the
 * candidate's viewer always open their own; admins follow the link only when it is one of theirs, otherwise their
 * primary assembly; the super admin follows the link or picks. A link to another assembly is not an error — it is
 * simply not used (the login's own assembly opens).
 */
export function chooseAssembly(user: Pick<FieldUser, "role" | "assemblyId" | "assemblyIds">, link: string | null): AssemblyChoice {
  if (user.role === "surveyor" || user.role === "viewer") {
    return { assemblyId: user.assemblyId, linkMismatch: false };
  }
  if (user.role === "assembly_admin") {
    const ids = user.assemblyIds ?? (user.assemblyId ? [user.assemblyId] : []);
    if (link && ids.includes(link)) return { assemblyId: link, linkMismatch: false };
    return { assemblyId: user.assemblyId ?? ids[0] ?? null, linkMismatch: false };
  }
  return { assemblyId: link, linkMismatch: false };
}

/** The war room's polling-day link: `?a=HAR_01&dday=1&k=<key>` (optionally `&b=45` for one booth). */
export interface DdayLink {
  key: string;
  booth: string | null;
}

export function parseDdayLink(search: string): DdayLink | null {
  const q = new URLSearchParams(search);
  const key = (q.get("k") ?? "").trim();
  if (!/^[A-Za-z0-9]{10,40}$/.test(key)) return null;
  const b = (q.get("b") ?? "").trim().toUpperCase();
  return { key, booth: /^\d{1,4}[A-Z]?$/.test(b) ? b : null };
}

/** A vehicle's driver link: `?a=HAR_01&drive=<key>`. */
export function parseDriveLink(search: string): string | null {
  const key = (new URLSearchParams(search).get("drive") ?? "").trim();
  return /^[A-Za-z0-9]{10,40}$/.test(key) ? key : null;
}
